import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { SpinnerGap, FloppyDisk } from "@phosphor-icons/react";
import { FieldLabel, ProfileInput, TONE_OPTIONS, AI_MODELS } from "./shared";

export function DefaultsTab({ profile, loadingProfile, updateField, saveProfile, savingProfile, toggleTone }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="defaults-tab">
      <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-6">
        <h2 className="font-['Cormorant_Garamond'] text-lg font-semibold text-[#FF4400] mb-1">Default Form Values</h2>
        <p className="text-sm text-[var(--text-muted)] mb-6">Pre-fill your generator pages so you can jump straight to writing.</p>

        {loadingProfile ? (
          <div className="flex justify-center py-12"><SpinnerGap size={28} className="animate-spin text-[#FF4400]" /></div>
        ) : (
          <div className="space-y-5">
            <ProfileInput label="Default Audience" value={profile?.default_audience || ""} onChange={(v) => updateField("default_audience", v)} placeholder="e.g. Small business owners, executives, coaches..." />

            <div>
              <FieldLabel label="Default Tone (up to 3)" />
              <div className="flex flex-wrap gap-2">
                {TONE_OPTIONS.map((tone) => {
                  const selected = (profile?.default_tone_variations || []).includes(tone);
                  return (
                    <button key={tone} onClick={() => toggleTone(tone)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                        selected ? "bg-[var(--accent-subtle)] text-[#FF4400] border-[#FF4400]/40" : "bg-[var(--bg-base)] text-[var(--text-muted)] border-[var(--border-strong)] hover:border-[var(--border-strong)]"
                      }`}
                    >{tone}</button>
                  );
                })}
              </div>
            </div>

            <div>
              <FieldLabel label="Default AI Model" />
              <select className="w-full bg-[var(--bg-base)] border border-[var(--border-strong)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-[#FF4400]"
                value={profile?.default_ai_model || ""} onChange={(e) => updateField("default_ai_model", e.target.value)}>
                <option value="">No default</option>
                {AI_MODELS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <ProfileInput label="Default CTA" value={profile?.default_cta || ""} onChange={(v) => updateField("default_cta", v)} placeholder="Book a strategy call" />
              <ProfileInput label="Default Offer / Asset" value={profile?.default_offer_asset || ""} onChange={(v) => updateField("default_offer_asset", v)} placeholder="e.g. Free strategy session" />
              <ProfileInput label="Default Platform" value={profile?.default_platform || ""} onChange={(v) => updateField("default_platform", v)} placeholder="LinkedIn" />
              <ProfileInput label="Default Content Type" value={profile?.default_content_type || ""} onChange={(v) => updateField("default_content_type", v)} placeholder="Short-form thought leadership" />
            </div>
            <ProfileInput label="Default Newsletter Name" value={profile?.default_newsletter_name || ""} onChange={(v) => updateField("default_newsletter_name", v)} placeholder="The Pipeline Post" />
            <ProfileInput label="Default Length Constraint" value={profile?.default_length_constraint || ""} onChange={(v) => updateField("default_length_constraint", v)} placeholder="Under 800 words" />

            {/* Sequence defaults */}
            <div className="border-t border-[var(--border)] pt-5 mt-2">
              <p className="font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[var(--text-muted)] mb-4">Sequence Planner Defaults</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <FieldLabel label="Awareness Stage" />
                  <select className="w-full bg-[var(--bg-base)] border border-[var(--border-strong)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-[#FF4400]"
                    value={profile?.default_awareness_stage || "cold"} onChange={(e) => updateField("default_awareness_stage", e.target.value)}>
                    <option value="cold">Cold</option>
                    <option value="problem-aware">Problem Aware</option>
                    <option value="solution-aware">Solution Aware</option>
                    <option value="product-aware">Product Aware</option>
                  </select>
                </div>
                <ProfileInput label="Content Count" value={String(profile?.default_content_count ?? 7)} onChange={(v) => updateField("default_content_count", parseInt(v) || 7)} placeholder="7" />
              </div>
            </div>

            {/* Lead Magnet defaults */}
            <div className="border-t border-[var(--border)] pt-5 mt-2">
              <p className="font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[var(--text-muted)] mb-4">Lead Magnet Defaults</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <FieldLabel label="Lead Magnet Type" />
                  <select className="w-full bg-[var(--bg-base)] border border-[var(--border-strong)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-[#FF4400]"
                    value={profile?.default_lead_magnet_type || "mini-guide"} onChange={(e) => updateField("default_lead_magnet_type", e.target.value)}>
                    <option value="mini-guide">Mini Guide</option>
                    <option value="checklist">Checklist</option>
                    <option value="cheat-sheet">Cheat Sheet</option>
                    <option value="template-pack">Template Pack</option>
                    <option value="workbook">Workbook</option>
                    <option value="swipe-file">Swipe File</option>
                  </select>
                </div>
                <ProfileInput label="Section Count" value={String(profile?.default_section_count ?? 4)} onChange={(v) => updateField("default_section_count", parseInt(v) || 4)} placeholder="4" />
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <Button onClick={saveProfile} disabled={savingProfile} className="bg-[#FF4400] hover:bg-[#FF6633] text-white gap-2" data-testid="save-defaults-btn">
                {savingProfile ? <SpinnerGap size={16} className="animate-spin" /> : <FloppyDisk size={16} weight="bold" />}
                Save Defaults
              </Button>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}
